import React from 'react';
import {
    Box,
    Button,
    ButtonProps,
    Dialog,
    DialogProps,
    IconButton,
    Slide,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { TransitionProps } from '@mui/material/transitions';
import { FONT_ENUM } from '../../types/global/fonts.types';
import Close from '@mui/icons-material/Close';

export interface IContentCreator {
    onClose?: () => void;
    open: boolean;
    onSubmit?: (...args: any) => void;
    children: JSX.Element;
    buttonEnabled?: boolean;
    buttonText?: string;
    paperSX?: SxProps<Theme>;
    dialogueSX?: SxProps<Theme>;
    buttonProps?: ButtonProps;
    /** Passed to MUI Dialog; `open` / `onClose` come from this component. */
    dialogueProps?: Omit<DialogProps, 'open' | 'onClose' | 'children'>;
    header?: string;
    headerStyle?: React.CSSProperties;
    /** Distinct top bar + scrollable body; close moves into header (e.g. Apply to role). */
    headerBar?: boolean;
    /** Shown under the header title when `headerBar` is set. */
    headerSubtitle?: string;
    /** Merged into the header title `Typography` when `headerBar` is set. */
    headerTypographySx?: SxProps<Theme>;
}

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});
/**
 * Custom Dialogue Modal that replaces The MUI Dialogue
 * @param props
 * @returns
 */

const DialogueModal: React.FC<IContentCreator> = props => {
    const {
        open,
        onClose,
        onSubmit,
        children,
        buttonEnabled,
        buttonText,
        paperSX,
        dialogueSX,
        buttonProps,
        header,
        headerStyle,
        dialogueProps,
        headerBar,
        headerSubtitle,
        headerTypographySx,
    } = props;
    const { themeStyle } = useThemeContext();
    const renderProjct = (
        <Dialog
            {...dialogueProps}
            scroll="paper"
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
            fullWidth={true}
            maxWidth={'lg'}
            slotProps={{
                backdrop: {
                    sx: {
                        opacity: 0.2,
                        backdropFilter: 'blur(8px)',
                    },
                },
                ...dialogueProps?.slotProps,
            }}
            PaperProps={{
                ...dialogueProps?.PaperProps,
                sx: [
                    {
                        backgroundColor: theme[themeStyle].deep_grey_lighter,
                        width: '100vw',
                        maxWidth: {
                            xs: '90%',
                            sm: '500px',
                        },
                        borderRadius: 5,
                        padding: headerBar ? 0 : '30px',
                        margin: '0px',
                        marginTop: headerBar ? 0 : '30%',
                        border: `0.5px solid ${theme[themeStyle].darkGray60}`,
                        overflow: headerBar ? 'hidden' : undefined,
                    },
                    (dialogueProps?.PaperProps as { sx?: SxProps<Theme> } | undefined)?.sx,
                    paperSX,
                ] as SxProps<Theme>,
            }}
            sx={
                [
                    {
                        display: 'flex',
                        height: headerBar ? '100%' : { xs: '80%', sm: '70%', lg: '70%' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        outline: 'none',
                    },
                    dialogueSX,
                    dialogueProps?.sx,
                ] as SxProps<Theme>
            }>
            {!headerBar && (
                <Button
                    sx={{
                        position: 'absolute',
                        minWidth: '5px',
                        top: '2%',
                        right: '5%',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                    onClick={onClose}>
                    <Close
                        sx={{
                            color: theme[themeStyle].tag_white,
                            width: '30px',
                            height: '30px',
                        }}
                    />
                </Button>
            )}

            {headerBar && !!header ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 1,
                        px: { xs: 2, sm: 4 },
                        pt: { xs: 3, sm: 4 },
                        pb: 2,
                        bgcolor: '#20201f',
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                    }}>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                        <Typography
                            sx={[
                                {
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                    color: theme[themeStyle].tag_white,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.04em',
                                    lineHeight: 1.2,
                                    ...headerStyle,
                                },
                                ...(headerTypographySx
                                    ? Array.isArray(headerTypographySx)
                                        ? headerTypographySx
                                        : [headerTypographySx]
                                    : []),
                            ]}>
                            {header}
                        </Typography>
                        {headerSubtitle ? (
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    fontSize: '0.875rem',
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: 'rgba(255,255,255,0.55)',
                                    lineHeight: 1.45,
                                }}>
                                {headerSubtitle}
                            </Typography>
                        ) : null}
                    </Box>
                    <IconButton
                        onClick={onClose}
                        aria-label="Close"
                        size="small"
                        sx={{
                            color: 'rgba(255,255,255,0.55)',
                            mt: -0.5,
                            '&:hover': {
                                color: theme[themeStyle].tag_white,
                                bgcolor: 'rgba(255,255,255,0.06)',
                            },
                        }}>
                        <Close sx={{ fontSize: 26 }} />
                    </IconButton>
                </Box>
            ) : (
                !!header && (
                    <Typography
                        style={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: 18,
                            color: theme[themeStyle].tag_white,
                            marginTop: '15px',
                            ...headerStyle,
                        }}>
                        {header}
                    </Typography>
                )
            )}
            {headerBar ? (
                <Box
                    sx={{
                        p: { xs: 2, sm: 3 },
                        maxHeight: { xs: 'calc(100dvh - 72px)', sm: 'min(870px, calc(90vh - 72px))' },
                        overflowY: 'auto',
                        boxSizing: 'border-box',
                    }}>
                    {children}
                </Box>
            ) : (
                children
            )}
            {buttonEnabled && (
                <Button
                    onClick={onSubmit}
                    style={{
                        height: '52px',
                        borderRadius: 100,
                        fontSize: 16,
                        fontWeight: '600',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '56px',
                    }}
                    sx={{
                        textDecoration: 'none',
                        backgroundColor: theme[themeStyle].primary_orange,
                        color: theme[themeStyle].pure_black,
                        textTransform: 'none',
                        width: '100%',
                        '&:hover': {
                            border: `2px solid ${theme[themeStyle].primary_orange}`,
                            backgroundColor: theme[themeStyle].pure_black,
                            color: theme[themeStyle].primary_orange,
                        },
                    }}
                    {...buttonProps}>
                    {buttonText || 'Submit'}
                </Button>
            )}
        </Dialog>
    );

    return renderProjct;
};

export { DialogueModal };
